import React, { useState, useCallback, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";

// Same-origin worker so PDFs work on mobile (many mobile browsers block cross-origin workers from unpkg)
const base = typeof process !== "undefined" && process.env?.PUBLIC_URL ? process.env.PUBLIC_URL : "";
pdfjs.GlobalWorkerOptions.workerSrc = `${base}/pdf.worker.min.mjs`;

function PdfPreview({ pdfUrl, pdfLoading, targetPage, readingLinePct, highlightWord, onPageClick }) {
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const pageRefs = useRef([]);
  const targetPageRef = useRef(null);

  // Keep a ref in sync so onLoadSuccess can access the latest value without a stale closure
  useEffect(() => { targetPageRef.current = targetPage; }, [targetPage]);

  const onLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    // When a new PDF loads (e.g. annotated version swaps in), re-scroll to the last target page
    if (targetPageRef.current) {
      setTimeout(() => {
        const page = Math.abs(targetPageRef.current);
        const el = pageRefs.current[page - 1];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, []);

  // Scroll to the target page when the reader pauses.
  // Negative values are used to force a re-scroll to the same page (after annotated PDF swaps in).
  useEffect(() => {
    if (!targetPage) return;
    const page = Math.abs(targetPage);
    const el = pageRefs.current[page - 1];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [targetPage]);

  // Reference to the pdf-scroll container so we can clear highlights across all pages
  const scrollRef = useRef(null);

  // Highlight the exact word in the PDF text layer
  useEffect(() => {
    if (!targetPage || !highlightWord) return;
    const timer = setTimeout(() => {
      const pageEl = pageRefs.current[Math.abs(targetPage) - 1];
      if (!pageEl) return;

      // Clear any previous highlight across ALL pages (not just the current one)
      const root = scrollRef.current || pageEl.closest(".pdf-scroll") || pageEl.parentElement;
      root.querySelectorAll(".pdf-span-highlight").forEach(el => el.classList.remove("pdf-span-highlight"));

      // react-pdf v10 uses both class names on the same element — try the original name first
      const textLayer =
        pageEl.querySelector(".react-pdf__Page__textContent") ||
        pageEl.querySelector(".textLayer");
      if (!textLayer) return;

      // Prefer positioned spans (have inline style.top set by PDF.js).
      // Exclude spans in the right margin (>74% from left) — annotation boxes
      // drawn by fitz end up there and would corrupt the occurrence count.
      // Fall back to all non-empty spans if none have top set.
      const isMainBody = (s) => {
        if (!s.style.top || !s.textContent.trim()) return false;
        const leftStr = s.style.left;
        if (leftStr && leftStr.endsWith('%') && parseFloat(leftStr) > 74) return false;
        return true;
      };
      let spans = [...textLayer.querySelectorAll("span")].filter(isMainBody);
      if (spans.length === 0) {
        spans = [...textLayer.querySelectorAll("span")].filter(s => s.textContent.trim());
      }

      const escaped = highlightWord.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      if (!escaped) return;

      const allWords = [];
      for (const span of spans) {
        for (const w of span.textContent.split(/\s+/).filter(Boolean)) {
          allWords.push({ span, clean: w.replace(/[^a-zA-Z0-9]/g, "").toLowerCase() });
        }
      }
      if (allWords.length === 0) return;

      // readingLinePct is repurposed as the Nth occurrence of this word within the page
      const occurrenceNum = readingLinePct ?? 0;

      // Collect all exact-match occurrence indices
      const occurrences = allWords.reduce((acc, entry, i) => {
        if (entry.clean === escaped) acc.push(i);
        return acc;
      }, []);

      // Fallback: partial match for hyphenated words, ligatures, etc.
      const candidates = occurrences.length > 0
        ? occurrences
        : allWords.reduce((acc, entry, i) => {
            if (entry.clean.includes(escaped) && escaped.length > 2) acc.push(i);
            return acc;
          }, []);

      if (candidates.length === 0) return;

      // Pick the Nth occurrence; clamp to last if out of bounds
      const target = candidates[Math.min(occurrenceNum, candidates.length - 1)];
      allWords[target].span.classList.add("pdf-span-highlight");
    }, 500);
    return () => clearTimeout(timer);
  }, [targetPage, highlightWord, readingLinePct]);

  const zoom = (delta) =>
    setScale((s) => Math.min(2.5, Math.max(0.5, +(s + delta).toFixed(2))));

  const handlePageClick = useCallback((e, pageNum) => {
    if (!onPageClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const yPct = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const xPct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onPageClick(pageNum, yPct, xPct);
  }, [onPageClick]);


  if (!pdfUrl) {
    if (pdfLoading) {
      return (
        <div className="pdf-placeholder">
          <div className="pdf-loading-spinner" aria-label="Loading PDF" />
          <p className="pdf-hint">Loading PDF…</p>
        </div>
      );
    }
    return (
      <div className="pdf-placeholder">
        <p>Your PDF will appear here.</p>
        <p className="pdf-hint">Upload a PDF to get started.</p>
      </div>
    );
  }

  // ── Viewer ───────────────────────────────────────────────
  return (
    <div className="pdf-viewer">
      {/* Toolbar */}
      <div className="pdf-toolbar">
        <span className="pdf-page-info">
          {numPages ? `${numPages} page${numPages !== 1 ? "s" : ""}` : "…"}
        </span>

        <div className="pdf-zoom">
          <button className="pdf-zoom-btn" onClick={() => zoom(-0.15)}>
            −
          </button>
          <span className="pdf-zoom-label">{Math.round(scale * 100)}%</span>
          <button className="pdf-zoom-btn" onClick={() => zoom(0.15)}>
            +
          </button>
        </div>

      </div>

      {/* Pages */}
      <div className="pdf-scroll" ref={scrollRef}>
        <Document
          file={pdfUrl}
          onLoadSuccess={onLoadSuccess}
          loading={<div className="pdf-loading">Loading…</div>}
          error={<div className="pdf-loading">Failed to load PDF.</div>}
        >
          {numPages &&
            Array.from({ length: numPages }, (_, i) => (
              <div
                className={`pdf-page-wrapper${targetPage === i + 1 ? " pdf-page-active" : ""}`}
                key={i + 1}
                ref={el => { pageRefs.current[i] = el; }}
                onClick={onPageClick ? (e) => handlePageClick(e, i + 1) : undefined}
                style={onPageClick ? { cursor: "pointer" } : undefined}
              >
                <Page
                  pageNumber={i + 1}
                  scale={scale}
                  renderAnnotationLayer={false}
                  renderTextLayer={true}
                />
              </div>
            ))}
        </Document>
      </div>
    </div>
  );
}

export default PdfPreview;
